import { useState, useCallback, useEffect, useRef } from "react";
import isEqual from "lodash.isequal";
import set from "lodash.set";
import { useDataSettings } from "./useData";
import { refManager } from "../db";
import { useObserveDbTable } from "./useMemoryDB";
import { doBlockRefs } from "../components/block";
import { doEndpointRefs } from "../components/endpoint";
import { doRuleRefs } from "../components/businessRule";
import { doExperimentRefs } from "../components/experiments";
import { getQueryRefs } from "../components/query/refs";
import { doPresetRefs } from "../components/rulesPreset";
import { doListRefs } from "../components/list";
import { doConversionPackRefs } from "../components/conversionPacks";
import { doConversionRefs } from "../components/conversions";
import { doExternalSourceRefs } from "../components/externalSource";
import { doParamsRefs } from "../components/params";
import { doMetaPackRefs } from "../components/metaPacks";

const emptyObj = {};

export default refManager;

export function useDoRef() {
  const queryClauses = useDataSettings("queryClauses");

  return useCallback(
    (accessor, obj) => {
      if (accessor === "blocks") doBlockRefs(obj);
      if (accessor === "endpoints") doEndpointRefs(obj);
      if (accessor === "rules") doRuleRefs(obj);
      if (accessor === "experiments") doExperimentRefs(obj);
      if (accessor === "rulesPresets") doPresetRefs(obj);
      if (accessor === "lists") doListRefs(obj);
      if (accessor === "conversionPacks") doConversionPackRefs(obj);
      if (accessor === "conversions") doConversionRefs(obj);
      if (accessor === "externalSources") doExternalSourceRefs(obj);
      if (accessor === "params") doParamsRefs(obj);
      if (accessor === "metaPacks") doMetaPackRefs(obj);
      // The same refs as the saved query (doQueryRefs), without copying the
      // whole query and applying its defaults only to read them.
      if (accessor === "queries")
        set(obj, "refs.require", getQueryRefs(obj, queryClauses));

      return obj;
    },
    [queryClauses]
  );
}

// `delay` (ms), for display-only consumers: the info is computed only once
// `obj` hasn't changed for `delay` ms (the first time right away).
// `keepEqual`: return the previous info while its content doesn't change, so
// the component doesn't re-render at every change of `obj`. Only for
// consumers that never mutate the info.
//
// `obj` is never changed: its refs (refs.require, prevReference: doRefs;
// refsComputed: fillBelongs) are computed on a copy. obj is often the Formik
// values, which share their objects with the initial values and the item of
// the form: stamped there, the item no longer equalled its stored row, and a
// re-read of that row, unchanged (db.dispatchAll() after every load, a
// restart of the worker), reinitialised the form and discarded its edits.
// What a Save sends computes its refs itself (the Item's onSubmit).
export function useDataRefObj(
  accessor,
  obj,
  { delay = 0, keepEqual = false } = {}
) {
  const doRefs = useDoRef();
  const [data, setData] = useState(emptyObj);
  const computedRef = useRef(false);

  useEffect(() => {
    if (obj && accessor) {
      const computeInfo = () => {
        // A shallow copy is enough: doRefs and fillBelongs set keys of the
        // root and of refs only
        const target = { ...obj };
        if (obj.refs && typeof obj.refs === "object")
          target.refs = { ...obj.refs };
        doRefs(accessor, target);

        const data = new Map([[accessor, [target]]]);
        const ret = refManager.fillBelongs(data, null, null, false);
        const item = ret.get(accessor)[0];
        const refs = refManager.process(accessor, item);
        const info = refManager.getItem(accessor, refs);
        setData(
          keepEqual
            ? (previous) => (isEqual(previous, info) ? previous : info)
            : info
        );
      };

      if (!delay || !computedRef.current) {
        computedRef.current = true;
        computeInfo();
        return;
      }

      const timer = setTimeout(computeInfo, delay);
      return () => clearTimeout(timer);
    }
  }, [accessor, delay, doRefs, keepEqual, obj]);

  return data;
}

// REACTIVE HOOKS
function useReactToDBChange(accessor) {
  const [change, setChange] = useState(Date.now());
  useObserveDbTable(accessor, () => setChange(Date.now()));

  return change;
}

export function useDataItemKV(accessor) {
  const change = useReactToDBChange(accessor);

  const fetch = useCallback(
    async (id, cb) => {
      if (id && accessor) {
        const data = refManager.getAllKW(accessor, { id });
        if (cb) return cb({ id, label: data[0]?.value });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accessor, change]
  );

  return fetch;
}

export function useDataItems(accessor) {
  const change = useReactToDBChange(accessor);

  const fetch = useCallback(
    (filters, cb) => {
      if (!cb) return [];
      const data = refManager.getAll(accessor, filters);
      cb(data);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accessor, change]
  );

  return fetch;
}

export function useDataItemsKV(accessor) {
  const change = useReactToDBChange(accessor);

  const fetch = useCallback(
    async (filters, cb) => {
      if (!cb) return;

      const data = refManager.getAllKW(accessor, filters);
      return cb(data);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accessor, change]
  );

  return fetch;
}

export function useDataItemsIds(accessor, filters, returnType = "array") {
  const [ids, setIds] = useState(returnType === "array" ? [] : new Set());
  const change = useReactToDBChange(accessor);

  useEffect(() => {
    const data = refManager.getAllIds(accessor, filters);
    setIds(returnType === "array" ? data : new Set(data));
  }, [accessor, filters, returnType, change]);

  return ids;
}

export function useDataRefItem(accessor, id) {
  const [data, setData] = useState(emptyObj);
  const change = useReactToDBChange(accessor);

  useEffect(() => {
    if (id && accessor) {
      const data = refManager.getAll(accessor, { id });
      setData(data[0]);
    }
  }, [accessor, id, change]);

  return data;
}
